import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, hotMovies } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'movies');
  const [filteredMovies, setFilteredMovies] = useState(hotMovies);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredMovies(hotMovies);
    } else {
      setFilteredMovies(hotMovies.filter((m) => m.category === activeCategory));
    }
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '全部';

  return (
    <>
      <Navbar />
      <main>
        <nav className="pt-20 pb-4 px-4 md:px-6 bg-background-50" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><span className="text-foreground-700">{categoryName}</span></li>
            </ol>
          </div>
        </nav>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              {categoryName}资源大全
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              茄子视频为您精选最值得观看的<strong>{categoryName}</strong>内容，<strong>高清免费不缓冲</strong>，畅享观影体验
            </p>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              <Link
                to="/category/all"
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-background-200 mb-3">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      title={`${movie.title} - 高清免费在线观看`}
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-background-950/80 backdrop-blur-sm text-accent-400 text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      {movie.rating}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-white/80 bg-primary-500/90 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {movie.categoryName}
                        </span>
                        <span className="text-xs text-white/70 whitespace-nowrap">{movie.year}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1">{movie.description}</p>
                </Link>
              ))}
            </div>

            {filteredMovies.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}